import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { BlogPost } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeleteBlogPost } from '../hooks/use-blog-post-mutations';

export interface DeleteBlogPostDialogProps {
  storeId: string;
  row: BlogPost | null;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}

export function DeleteBlogPostDialog({
  storeId,
  row,
  onOpenChange,
  onDeleted,
}: DeleteBlogPostDialogProps) {
  const { t } = useTranslation(['blog', 'common', 'errors']);
  const remove = useDeleteBlogPost(storeId);
  const message = useErrorMessage();
  const requestId = isApiError(remove.error) ? remove.error.requestId : undefined;

  return (
    <AlertDialog
      open={row !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('blog:deletePost.title', { title: row?.title ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('blog:deletePost.description', { handle: row?.handle ?? '' })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {message(remove.error)}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (row) {
                remove.mutate(row.id, {
                  onSuccess: () => {
                    onOpenChange(false);
                    onDeleted?.();
                  },
                });
              }
            }}
          >
            {remove.isPending ? t('blog:deletePost.submitting') : t('blog:deletePost.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
